<template>
  <ScrollArea>
    <div
      v-if="forbidden" >
      <h1 class="settings__title">
        My notification preferences
      </h1>
      <Forbidden />
    </div>
    <div
      v-if="!forbidden"
      class="settings--limit-width">
      <h1 class="settings__title">
        My notification preferences
      </h1>

      <Spinner
        v-if="userSettingsLoading"
        class="mt-5"
        :thickness="2" />

      <template v-else>
        <ValidationObserver
          ref="customFieldObserver"
          v-slot="{ valid, dirty, pristine }" >

          <b-form-group>
            <div
              v-for="(notification, notificationIndex) in notifications"
              :key="notificationIndex" >
              <div
                class="checkbox-provider flex flex-align-center" >
                <p :inner-html.prop="notification.title | boldMatch('new messages') | boldMatch('unassigned') | boldMatch('assigned to') | boldMatch('approval status') | boldMatch('others') | boldMatch('me')" />
                <SBIcon
                  v-if="notification.description"
                  :id="`notification-${notificationIndex}-description`"
                  class="ml-1"
                  :icon="SBIconList.INFORMATION"
                  :color="SBColors.greyPrimary"
                  :size="12" />
                <b-popover
                  :target="`notification-${notificationIndex}-description`"
                  triggers="hover"
                  placement="bottom"
                  :delay="{ show: 200, hide: 0 } " >
                  {{ notification.description }}
                </b-popover>
                <span class="greedy" />
                <ValidationProvider
                  v-for="(platform, index) in notification.platforms"
                  :key="index"
                  class="flex flex-align-center"
                  tag="div" >
                  <b-form-checkbox
                    v-model="notificationsModel[platform.id]"
                    :class="''" >
                    <div class="flex center-all">
                      <h3
                        class="no-wrap"
                        :inner-html.prop="platform.title" />
                    </div>
                  </b-form-checkbox>

                </ValidationProvider>
              </div>
            </div>
          </b-form-group>

          <div
            v-if="errorMessage"
            class="error-message">
            {{ errorMessage }}
          </div>

          <FooterActions
            :form-valid="valid"
            :has-changes="dirty"
            :show-cancel="!pristine"
            :save-busy="saving"
            no-top-border
            @reset="resetChanges"
            @save="updateNotificationSettings" />
        </ValidationObserver>
      </template>
    </div>
  </ScrollArea>
</template>

<script>
import { mapGetters, mapActions } from 'vuex';
import { extend } from 'vee-validate';
import { required } from 'vee-validate/dist/rules';

import ToasterMixin from '@/mixins/Toaster';
import Spinner from '../../common/Spinner';
import FooterActions from './partials/FooterActions';
import ScrollArea from '@/components/common/ScrollArea';
import NoPermissionMixin from '@/mixins/AccessPermission';
import SBColors from '@/helpers/lists/colors';
import SBIcon from '@/components/common/Icon';
import SBIconList from '@/helpers/lists/sb-icon-list';

extend('required', required);

export default {
  name: 'NotificationSettings',
  components: {
    FooterActions,
    Spinner,
    ScrollArea,
    SBIcon,

  },
  mixins: [
    ToasterMixin,
    NoPermissionMixin,
  ],
  data() {
    return {
      saving: false,
      errorMessage: '',
      permissionRoleChanges: {},
      tempSavingRoles: null,

      notifications: {},
      notificationsModel: {},
      tempNotifications: {},
      SBColors,
      SBIconList,
    };
  },
  computed: {
    ...mapGetters({
      apiError: 'merchant/apiError',
      userSettingsLoading: 'user/userSettingsLoading',
    }),
    /** Test the change on the model with the last save data and disables the button if they are the same. */
    testIfFormChanged() {
      return this.$lodash.isEqual({ ...this.tempNotifications }, { ...this.notificationsModel });
    },
  },
  watch: {
    apiError(error) {
      this.saving = false;
      this.errorMessage = error.data.message;
    },
    // Since we are not using the testIfFormChanged computed anywhere in the code it doesn't get computed so we have to watch on it.
    // Also we can't watch on permissionRoleChanges because it is not reactive since we add the keys dinamically. Even if we add the keys with $set method it is still not dynamic since it won't react on array changes.
    // That is why we need this weird construction of computed which reacts to all the changes which we then react to in the watch section.
    testIfFormChanged() {
      if (this.testIfFormChanged && this.$refs.customFieldObserver) {
        this.$refs.customFieldObserver.reset();
      }
    },
  },
  mounted() {
    this.fetchNotificationSettings().then(this.processNotificationSettings);
    this.finalizeSplashView();
  },
  methods: {
    ...mapActions({
      updatePermissions: 'merchant/updatePermissions',
      finalizeSplashView: 'organisation/finalizeSplashView',

      fetchNotificationSettings: 'user/fetchNotificationSettings',
      updateNotificationSettingsVuex: 'user/updateNotificationSettings',
    }),
    resetChanges() {
      this.notificationsModel = { ...this.tempNotifications };
      if (this.$refs.customFieldObserver) {
        this.$nextTick(() => {
          this.$refs.customFieldObserver.reset();
        });
      }
    },
    processNotificationSettings(response) {
      response.data.notifications.forEach((notification) => {
        notification.platforms.sort((a, b) => {
          const titlaA = a.title.toUpperCase();
          const titlaB = b.title.toUpperCase();
          if (titlaA < titlaB) {
            return -1;
          }
          if (titlaA > titlaB) {
            return 1;
          }
          return 0;
        });
      });
      this.notifications = response.data.notifications;

      // Setting user notification to model
      response.data.user_notifications.forEach((userNotification) => {
        this.tempNotifications[userNotification.id] = userNotification.active;
      });

      // check if user has all notifications set and set it if needed
      response.data.notifications.forEach((notification) => {
        notification.platforms.forEach((platform) => {
          if (!this.tempNotifications[platform.id]) {
            this.tempNotifications[platform.id] = false;
          }
        });
      });

      this.notificationsModel = { ...this.tempNotifications };
    },
    updateNotificationSettings() {
      this.saving = true;
      this.errorMessage = '';

      const notifications = [];
      Object.keys(this.notificationsModel).forEach((id) => {
        notifications.push({
          id,
          active: this.notificationsModel[id],
        });
      });

      const payload = {
        data: {
          notifications,
        },
      };

      this.updateNotificationSettingsVuex(payload)
        .then((response) => {
          if (response.status === 200) {
            this.showToaster('Notification preferences have been successfully updated.', null, null, 'OK');
            this.tempNotifications = { ...this.notificationsModel };
          } else {
            this.showToaster('Error while updateing notification preferences. Please try again.', null, null, 'error');
          }
        })
        .catch(() => {
          this.showToaster('Error while updateing notification preferences. Please try again.', null, null, 'error');
        })
        .finally(() => {
          this.saving = false;
        });
    },
  },
};
</script>

<style lang="scss" scoped>
::v-deep {
  .black {
    font-weight: 500 !important;
  }

  ul.multiselect__content {
    li.multiselect__element:first-of-type {
      pointer-events: none; // prevents user to unselect admin
      opacity: 0.7;
      color: $lightGray;
      // display: none;     // hides admin option - comment this and uncomment surounding properties for a disabled option
      cursor: default !important;

      span.multiselect__option, span.multiselect__option--selected {
        // Can't override it from here since a more specific selector is needed.
        // Check the override a few lines below.
      }
    }
  }

  .form-group {
    margin: 18px 0 0 0 !important;
  }

  .multiselect.no-selected-background .multiselect__content-wrapper .multiselect__content
    .multiselect__element:first-of-type .multiselect__option.multiselect__option--selected {
      color: $blackLight !important;

      .sb-icon .mask {
        --color: #{$blackLight} !important;
      }
    }

  .checkbox-provider {
    padding: 13px 0;
    border-bottom: solid 1px $greyBorderSecondary;

    .custom-control {
      display: flex;
      margin-left: 30px;

      .custom-control-label {
        display: flex;
      }
    }
  }
}
</style>
